/**
 * JSON-LD schema helpers — the runtime version.
 *
 * Scope: the helpers we actually wire today.
 *   - The sitewide Organization schema is an inline <script> in index.html
 *     (with @id) so it ships on every route including the SPA shell.
 *   - This module provides per-page schemas (WebSite, BreadcrumbList)
 *     rendered via <JsonLd> at SSG time.
 *
 * Adding a new schema (blog Article, FAQPage, MortgageLoan, etc.):
 *   Add the helper here, wire it to the page component. Don't add helpers
 *   for pages that don't exist yet — Google penalises schema that doesn't
 *   match visible page content.
 *
 * Validation before shipping any new schema:
 *   1. https://validator.schema.org/?url=<URL>
 *   2. https://search.google.com/test/rich-results?url=<URL>
 *   3. GSC → Enhancements — watch for errors after crawl.
 */

import { COMPANY } from "../../config/company";

/** Stable @id for the sitewide Organization established in index.html.
 *  Other schemas reference this with `{ "@id": ORG_REF }` rather than
 *  re-emitting Organization data, keeping the @graph clean. */
export const ORG_REF = `${COMPANY.url}/#organization`;

export interface BreadcrumbItem {
  name: string;
  url: string;
}

/**
 * WebSite schema — wire on / only.
 * Omits potentialAction (sitelinks searchbox) because /pesquisa doesn't
 * exist yet; dishonest searchbox signalling is worse than no signal.
 */
export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${COMPANY.url}/#website`,
    url: COMPANY.url,
    name: COMPANY.shortName,
    publisher: { "@id": ORG_REF },
    inLanguage: "pt-PT",
  };
}

/**
 * BreadcrumbList — wire on every non-homepage.
 * Each `name` should match the page's visible H1 or breadcrumb label.
 * Each `url` must be the absolute canonical URL of that step.
 */
export function breadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export interface FaqQa {
  q: string;
  a: string;
}

/**
 * FAQPage — wire on simulator pages, hub explainers, and any long-form
 * article that ends in a Q&A section. Drop ONE block per page (Google
 * picks the first and ignores duplicates).
 *
 * Each Q's `a` must be plain text. HTML inside acceptedAnswer.text is
 * allowed by spec but stripped to text in rendered results — keep the
 * string identical to what the user sees so the snippet matches.
 */
export function faqPageSchema(items: FaqQa[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
}

/**
 * Wraps multiple schemas into a single @graph block — one <script> tag
 * per page instead of many, which means one CSP hash per page instead of
 * many. Always prefer this over multiple <JsonLd> instances on one page.
 */
export function combineSchemas(...schemas: object[]) {
  return {
    "@context": "https://schema.org",
    "@graph": schemas.map((s) => {
      const { "@context": _omit, ...rest } = s as { "@context"?: unknown };
      return rest;
    }),
  };
}
